import classNames from "classnames";
import cuid from "cuid";
import { useFormik } from "formik";
import { get, isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazyQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { FRANCE_ADDRESS_ZIP_CODE_CITY } from "utils/api/graphql/queries/referentials";
import {
  BANK_ACCOUNT_OWNER_TYPE,
  CONTACT_POINT_SYSTEM,
  GENDER,
  PAYMENT_FREQUENCIES,
} from "utils/constants";
import { bindInputProps, setDefaultIfEmpty } from "utils/helpers/input";
import { ReactComponent as InfoIcon } from "../Assets/icons/info.svg";
import ActionButtons from "../Components/ActionButtons";
import Actions from "../Components/Actions";
import Button from "../Components/Button";
import Card from "../Components/Card";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

function View({
  initialValues,
  subscription,
  project,
  onNext,
  onBack,
  loading,
  translate,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });

  // Use local state to ensure body visibility updates immediately
  const [isPayerReceiver, setIsPayerReceiver] = useState(
    get(initialValues, "isPayerReceiver", true),
  );
  const [cities, setCities] = useState([]);
  const [addressZipCodeCity] = useLazyQuery(FRANCE_ADDRESS_ZIP_CODE_CITY);

  // Create validation schema dynamically based on isPayerReceiver
  const validationSchema = useMemo(
    () =>
      validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
        { isPayerReceiver: isPayerReceiver },
      ),
    [isPayerReceiver, t],
  );

  const { handleSubmit, values, setFieldValue, validateForm, ...formProps } =
    useFormik({
      initialValues: initialValues,
      onSubmit: onNext,
      enableReinitialize: true,
      validationSchema: validationSchema,
    });

  // Sync local state with Formik values and re-validate when isPayerReceiver changes
  useEffect(() => {
    const formikIsPayerReceiver = get(values, "isPayerReceiver", true);
    if (formikIsPayerReceiver !== isPayerReceiver) {
      setIsPayerReceiver(formikIsPayerReceiver);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.isPayerReceiver]);

  // Re-validate when isPayerReceiver changes
  useEffect(() => {
    if (validateForm) {
      validateForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPayerReceiver]);

  useEffect(() => {
    if (get(initialValues, "payment.payer.owner.isAdherent")) {
      const owner = {
        id: get(values, "payment.payer.owner.id", cuid()),
        firstName: get(project, "contact.user.firstname"),
        lastName: get(project, "contact.user.lastname"),
        isAdherent: true,
        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      };
      setFieldValue("payment.payer.owner", owner);
      if (get(values, "isPayerReceiver") === true) {
        setFieldValue("payment.receiver", {
          ...get(values, "payment.payer"),
          owner,
        });
      }
    }
    setIsPayerReceiver(get(initialValues, "isPayerReceiver", true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  // Fetch cities on mount if zip code exists
  useEffect(() => {
    const zipCode = get(
      values,
      `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
    );
    if (zipCode) {
      addressZipCodeCity({
        variables: {
          where: {
            zipCode: zipCode,
          },
        },
        onCompleted: ({ franceAddressZipCodeCity: { response } }) => {
          setCities(response);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      <Header
        title={
          translate?.HEADER?.TITLE ||
          "Renseignez vos informations<br />bancaires"
        }
        highlight={translate?.HEADER?.HIGHLIGHT || "Renseignez"}
        subtitle={translate?.HEADER?.SUBTITLE || "Informations bancaires"}
        onBack={onBack}
      />
      <div className="miltis-form">
        <div className="miltis-form__container">
          <div className="miltis-form__row">
            <Input
              placeholder={
                translate?.FORM?.ACCOUNT_HOLDER_LASTNAME?.PLACEHOLDER ||
                "ex : Doe"
              }
              label={
                translate?.FORM?.ACCOUNT_HOLDER_LASTNAME?.LABEL ||
                "Nom titulaire du compte"
              }
              {...bindInputProps({
                name: "payment.payer.owner.lastName",
                values,
                ...formProps,
              })}
            />
            <Input
              placeholder={
                translate?.FORM?.ACCOUNT_HOLDER_FIRSTNAME?.PLACEHOLDER ||
                "ex : John"
              }
              label={
                translate?.FORM?.ACCOUNT_HOLDER_FIRSTNAME?.LABEL ||
                "Prénom titulaire du compte"
              }
              {...bindInputProps({
                name: "payment.payer.owner.firstName",
                values,
                ...formProps,
              })}
            />
          </div>

          <div className="miltis-form__row">
            <Input
              placeholder={
                translate?.FORM?.IBAN?.PLACEHOLDER ||
                "ex : FR76 3000 4001 2600 0100 6123 456"
              }
              label={translate?.FORM?.IBAN?.LABEL || "IBAN"}
              {...bindInputProps({
                name: "payment.payer.IBAN",
                values,
                ...formProps,
              })}
            />
            <Input
              placeholder={translate?.FORM?.BIC?.PLACEHOLDER || "ex : PRNSFRP1"}
              label={translate?.FORM?.BIC?.LABEL || "BIC"}
              {...bindInputProps({
                name: "payment.payer.BIC",
                values,
                ...formProps,
              })}
            />
          </div>

          <div className="miltis-form__row">
            <Select
              options={setDefaultIfEmpty(
                get(subscription, "insurancePlan.fields.payment.frequency"),
                Object.values(PAYMENT_FREQUENCIES),
              ).map((value) => ({
                value,
                label: TRANSLATION.PAYMENT_FREQUENCY.OPTIONS[value],
              }))}
              label={TRANSLATION.PAYMENT_FREQUENCY.LABEL}
              placeholder={
                translate?.FORM?.SELECT_PLACEHOLDER || "Sélectionnez ..."
              }
              {...bindInputProps({
                name: "payment.frequency",
                values,
                ...formProps,
              })}
              // disabled={subscription?.locked}
            />
            <Select
              options={setDefaultIfEmpty(
                get(subscription, "insurancePlan.fields.payment.debitDay"),
                ["5", "12"],
              ).map((value) => ({
                value,
                label: value,
              }))}
              label={TRANSLATION.DEBIT_DAY.LABEL}
              placeholder={
                translate?.FORM?.SELECT_PLACEHOLDER || "Sélectionnez ..."
              }
              {...bindInputProps({
                name: "payment.debitDay",
                values,
                ...formProps,
              })}
              // disabled={subscription?.locked}
            />
          </div>

          <Actions
            label={
              translate?.ACTIONS?.QUESTION ||
              "Le compte de prélèvement des cotisations sera-t-il le même que le compte de remboursement des prestations ?"
            }
            actions={
              <>
                <Button
                  size="small"
                  onClick={() => {
                    if (subscription?.locked) return;
                    const payer = get(values, "payment.payer");
                    // Update local state immediately for UI reactivity
                    setIsPayerReceiver(true);

                    // Set isPayerReceiver to true in form values
                    setFieldValue("isPayerReceiver", true);

                    // Copy all payer information to receiver, including same ID
                    const receiverData = {
                      ...payer,
                      id: get(payer, "id"), // Same ID as payer
                      IBAN: get(payer, "IBAN", ""),
                      BIC: get(payer, "BIC", ""),
                      owner: {
                        ...get(payer, "owner", {}),
                        id: get(payer, "owner.id"),
                        firstName: get(payer, "owner.firstName", ""),
                        lastName: get(payer, "owner.lastName", ""),
                        isAdherent: get(payer, "owner.isAdherent", false),
                        type: get(
                          payer,
                          "owner.type",
                          BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
                        ),
                        gender: get(payer, "owner.gender", GENDER.MALE),
                      },
                    };

                    setFieldValue("payment.receiver", receiverData);
                  }}
                  type={classNames({
                    primary: isPayerReceiver === true,
                    white: isPayerReceiver === false,
                  })}
                >
                  {translate?.ACTIONS?.YES || "Oui"}
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    if (subscription?.locked) return;

                    // Update local state immediately for UI reactivity
                    setIsPayerReceiver(false);

                    setFieldValue("isPayerReceiver", false);
                    setFieldValue("payment.receiver", {
                      id: cuid(),
                      IBAN: "",
                      BIC: "",
                      owner: {
                        id: cuid(),
                        firstName: "",
                        lastName: "",
                        gender: GENDER.MALE,
                        isAdherent: false,
                        type: BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
                      },
                    });
                    // Vider explicitement chaque champ pour forcer la mise à jour
                    setFieldValue("payment.receiver.IBAN", "");
                    setFieldValue("payment.receiver.BIC", "");
                    setFieldValue("payment.receiver.owner.firstName", "");
                    setFieldValue("payment.receiver.owner.lastName", "");
                    setFieldValue("payment.receiver.owner.gender", null);
                  }}
                  type={classNames({
                    primary: isPayerReceiver === false,
                    white: isPayerReceiver === true,
                  })}
                >
                  {translate?.ACTIONS?.NO || "Non"}
                </Button>
              </>
            }
            body={
              !isPayerReceiver && (
                <Card bg="white">
                  {/* Information message when Non is selected */}
                  <div
                    className="miltis__info-message"
                    style={{ color: "#CA005D", marginBottom: "16px" }}
                  >
                    <InfoIcon
                      style={{ width: "24px", height: "24px", flexShrink: 0 }}
                    />
                    <span
                      style={{ fontSize: "12px", lineHeight: "1.5" }}
                      dangerouslySetInnerHTML={{
                        __html:
                          translate?.RECEIVER?.INFO_MESSAGE ||
                          "Vous serez invité(e) à fournir <strong>une copie de la pièce d'identité du payeur des primes</strong> ainsi que <strong>celle du bénéficiaire des prestations</strong>.",
                      }}
                    />
                  </div>

                  <Input
                    label={translate?.RECEIVER?.CIVILITE?.LABEL || "Civilité"}
                    type="radio"
                    row
                    values={[
                      {
                        value: GENDER.MALE,
                        label: translate?.RECEIVER?.CIVILITE?.MALE || "Homme",
                      },
                      {
                        value: GENDER.FEMALE,
                        label: translate?.RECEIVER?.CIVILITE?.FEMALE || "Femme",
                      },
                    ]}
                    {...bindInputProps({
                      name: "payment.receiver.owner.gender",
                      values,
                      ...formProps,
                    })}
                    onChange={(value) =>
                      setFieldValue("payment.receiver.owner.gender", value)
                    }
                  />

                  {/* Nom and Prénom */}
                  <div className="miltis-form__row">
                    <Input
                      label={translate?.RECEIVER?.LASTNAME?.LABEL || "Nom"}
                      placeholder={
                        translate?.RECEIVER?.LASTNAME?.PLACEHOLDER || "ex : Doe"
                      }
                      {...bindInputProps({
                        name: "payment.receiver.owner.lastName",
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      label={translate?.RECEIVER?.FIRSTNAME?.LABEL || "Prénom"}
                      placeholder={
                        translate?.RECEIVER?.FIRSTNAME?.PLACEHOLDER ||
                        "ex : John"
                      }
                      {...bindInputProps({
                        name: "payment.receiver.owner.firstName",
                        values,
                        ...formProps,
                      })}
                    />
                  </div>

                  {/* IBAN and BIC */}
                  <div className="miltis-form__row">
                    <Input
                      label={translate?.RECEIVER?.IBAN?.LABEL || "IBAN"}
                      placeholder={
                        translate?.RECEIVER?.IBAN?.PLACEHOLDER ||
                        "Ex : FR12 3456 7890 1234 5678 9012 345"
                      }
                      {...bindInputProps({
                        name: "payment.receiver.IBAN",
                        values,
                        ...formProps,
                      })}
                    />
                    <Input
                      label={translate?.RECEIVER?.BIC?.LABEL || "BIC"}
                      placeholder={
                        translate?.RECEIVER?.BIC?.PLACEHOLDER || "Ex : PRNSFRP1"
                      }
                      {...bindInputProps({
                        name: "payment.receiver.BIC",
                        values,
                        ...formProps,
                      })}
                    />
                  </div>

                  {/* Address */}
                  <div className="miltis-form__row">
                    <Input
                      label={
                        translate?.RECEIVER?.ADDRESS?.LABEL ||
                        TRANSLATION.ADDRESS.LABEL ||
                        "Adresse"
                      }
                      placeholder={
                        translate?.RECEIVER?.ADDRESS?.PLACEHOLDER ||
                        TRANSLATION.ADDRESS.PLACEHOLDER ||
                        "Ex : 123 Rue de la République"
                      }
                      {...bindInputProps({
                        name: `telecoms.receiver.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                    {/* Complement (optional) */}
                    <Input
                      label={
                        translate?.RECEIVER?.COMPLEMENT?.LABEL ||
                        TRANSLATION.COMPLEMENT.LABEL ||
                        "Complément d'adresse"
                      }
                      placeholder={
                        translate?.RECEIVER?.COMPLEMENT?.PLACEHOLDER ||
                        TRANSLATION.COMPLEMENT.PLACEHOLDER ||
                        "Ex : Appartement 4B"
                      }
                      optional={
                        translate?.RECEIVER?.OPTIONAL ||
                        TRANSLATION.OPTIONAL ||
                        "Optionnel"
                      }
                      {...bindInputProps({
                        name: `telecoms.receiver.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                  </div>

                  {/* Postal Code */}
                  <div className="miltis-form__row">
                    <Input
                      label={
                        translate?.RECEIVER?.POSTAL_CODE?.LABEL ||
                        TRANSLATION.POSTAL_CODE.LABEL ||
                        "Code postal"
                      }
                      placeholder={
                        translate?.RECEIVER?.POSTAL_CODE?.PLACEHOLDER ||
                        TRANSLATION.POSTAL_CODE.PLACEHOLDER ||
                        "Ex : 69001"
                      }
                      {...bindInputProps({
                        name: `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                        values,
                        ...formProps,
                      })}
                      onChange={({ target: { value } }) => {
                        setFieldValue(
                          `telecoms.receiver.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                          value,
                        );
                        // Clear city when zip code changes
                        setFieldValue(
                          `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
                          undefined,
                        );
                        // Trigger city fetch when zip code changes
                        if (value) {
                          addressZipCodeCity({
                            variables: {
                              where: {
                                zipCode: value,
                              },
                            },
                            onCompleted: ({
                              franceAddressZipCodeCity: { response },
                            }) => {
                              setCities(response);
                            },
                          });
                        } else {
                          setCities([]);
                        }
                      }}
                    />

                    {/* City */}
                    <Select
                      label={
                        translate?.RECEIVER?.CITY?.LABEL ||
                        TRANSLATION.CITY.LABEL ||
                        "Ville"
                      }
                      placeholder={
                        translate?.RECEIVER?.CITY?.PLACEHOLDER ||
                        TRANSLATION.CITY.PLACEHOLDER ||
                        "Sélectionnez une ville"
                      }
                      showSearch
                      options={cities}
                      {...bindInputProps({
                        name: `telecoms.receiver.${CONTACT_POINT_SYSTEM.CITY}.value`,
                        values,
                        ...formProps,
                      })}
                    />
                  </div>
                </Card>
              )
            }
            variant="false"
          />
          <ActionButtons
            disabled={!isEmpty(formProps.errors)}
            onNext={handleSubmit}
            onBack={onBack}
            loading={loading}
            buttonText={translate?.BUTTON?.CONTINUE || "Continuer"}
          />
          <DataProtection />
        </div>
      </div>
    </Layout>
  );
}

export default View;
